import React, { useState, useLayoutEffect, useRef, PointerEvent } from "react";
import { ClipInterval } from './types/ClipInterval';
import { formatTime } from '../Utils/ConversionUtils'
import "./css/ClipWindowContext.css";

export interface ClipWindowContextProps {
    videoDurationSecs: number;
    clip: ClipInterval;
    viewport: ClipInterval;
    onViewportChange?: (viewport: ClipInterval, clip: ClipInterval) => void;
}

export const ClipWindowContext: React.FC<ClipWindowContextProps> = ({
    videoDurationSecs,
    clip,
    viewport,
    onViewportChange,
}) => {
    // Defensive: avoid divide-by-zero
    const safeDuration = Math.max(videoDurationSecs, 0.0001);
    const videoBarRef = useRef<HTMLDivElement | null>(null);
    const [videoBarWidth, setVideoBarWidth] = useState(0);
    const [showViewportSelect, setShowViewportSelect] = useState(false);
    const [viewportSelect, setViewportSelect] = useState<ClipInterval | null>(null);

    useLayoutEffect(() => {
        if (videoBarRef.current) {
            setVideoBarWidth(videoBarRef.current.clientWidth);
        }
    }, []);

    const px = (secs: number) => (secs / safeDuration) * videoBarWidth;

    const viewportStyle = {
        left: `${px(viewport.start)}px`,
        width: `${px(viewport.duration)}px`,
    };

    const viewportSelectStyle = {
        left: `${px(viewportSelect?.start ?? 0)}px`,
        width: `${px(viewport.duration)}px`,
    };

    const onVideoBarPointerLeave = (e: PointerEvent<HTMLDivElement>) => {
        setShowViewportSelect(false);
    }

    const onVideoBarPointerMove = (e: PointerEvent<HTMLDivElement>) => {
        if (videoBarWidth && viewport && videoBarRef.current) {
            const rect = videoBarRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const start = (x / videoBarWidth) * videoDurationSecs;

            if (start < safeDuration - viewport.duration) {
                setViewportSelect({ start: start, duration: viewport.duration });
                setShowViewportSelect(true);
            }
        }
    }

    return (
        <div className="cwc-root">
            <div className="cwc-bar cwc-video-bar" ref={videoBarRef}
                onPointerMove={onVideoBarPointerMove}
                onPointerLeave={onVideoBarPointerLeave}
                onClick={(e) => {
                    if (viewportSelect) {
                        onViewportChange?.({ start: viewportSelect?.start, duration: viewport.duration }, { start: viewportSelect?.start,duration:clip.duration });
                    }
                }}>
                <div className="cwc-video-bar-inner">
                    <div className={`cwc-video-bar-select ${showViewportSelect ? "visible" : ""}`}
                        style={viewportSelectStyle} >
                        { viewportSelect &&
                            <div className="cwc-video-bar-select-timestamp">
                                <span>{formatTime(viewportSelect.start)}</span>
                                <span className="cwc-indicator"/>
                            </div>
                        }
                    </div>
                </div>
            </div>
            <div className="cwc-bar cwc-viewport-bar" style={viewportStyle} />
            <div className="cwc-bar cwc-clip-bar" style={({
                "--clipWidth": `${px(clip.duration)}px`,
                "--clipLeft": `${px(clip.start)}px`
            } as React.CSSProperties)} />
        </div>
    );
}

